import { useEffect, useMemo, useState } from 'react';

import {
	BookAltOutline,
	ChartBarOutline,
	ChatAltOutline,
	DocumentReportOutline,
	GiftOutline,
	HistoryOutline,
	LifeRaftOutline,
	OfficeBuildingOutline,
	PlantOutline,
	PuzzleOutline,
	ShieldCheckOutline,
	UserGroupOutline,
	ViewGridOutline,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { Permission, useCurrentCompany, useCurrentUser, useFeatureFlag, usePermission } from '@bonusly/who-am-i';

import { AppPath, Route } from '../../../config/router';
import { adminRewardsPath } from '../../../utils/api';
import { NavConfig, NavLinkConfig, NavSectionConfig } from '../types/nav';
import { filterNavLinks, filterNavSections } from '../utils/filter-nav-items';

/**
 * Builds a Kaleidoscope path for a Rails admin page that will be
 * rendered in an iframe via AdminLegacyPage. Paths under /admin/* or
 * /super_admin/* pass through; others get a /r/ prefix to avoid
 * conflicts with existing Kaleidoscope routes.
 */
function adminIframePath(railsPath: string, shell: '/admin' | '/super_admin' = '/admin'): string {
	const normalized = railsPath.startsWith('/') ? railsPath : `/${railsPath}`;
	if (normalized.startsWith('/admin/') || normalized.startsWith('/super_admin/')) return normalized;
	return `${shell}/r${normalized}`;
}

export const useNavOptions = () => {
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const recognizeURL = useBuildRecognizeURL();

	// Call all permission hooks at the top level for requirements
	const isOneOnOnesEnabled = usePermission(Permission.CanAccessOneOnOnes);
	const isRecapsEnabled = usePermission(Permission.CanAccessRecaps);
	const canManagePeerToPeer = usePermission(Permission.CanManagePeerToPeer);
	const canAccessAwards = usePermission(Permission.CanAccessAwards);
	const canAccessNominations = usePermission(Permission.CanAccessNominations);
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);
	const canAccessAnnouncements = usePermission(Permission.CanAccessAnnouncements);
	const canAccessUserManagement = usePermission(Permission.CanAccessUserManagement);
	const canManageUserImport = usePermission(Permission.CanManageUserImport);
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const canAccessAdminCheckInControls = usePermission(Permission.CanAccessAdminCheckInControls);
	const canViewContinuousCheckIns = usePermission(Permission.CanViewContinuousCheckIns);
	const canViewExpandedReporting = usePermission(Permission.CanViewExpandedReporting);
	const hasAnalyticsTier = usePermission(Permission.CanAccessAnalytics);
	const showLegacyReports = useFeatureFlag('show-legacy-reports');
	const rewardsReportFrontend = useFeatureFlag('rewards-report-frontend');
	const recognitionActivityRedesign = useFeatureFlag('recognition-activity-redesign');
	const canManageTeamRequests = usePermission(Permission.CanManageTeamRequests);
	const hasManagerAssignmentTool = useFeatureFlag('manager-assignment-tool');

	// Use state to track pathname so component re-renders on URL change
	const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

	// Listen for URL changes
	useEffect(() => {
		const handleLocationChange = () => {
			setCurrentPathname(window.location.pathname);
		};

		// Check immediately on mount
		handleLocationChange();

		// Listen for popstate events (browser back/forward)
		window.addEventListener('popstate', handleLocationChange);

		// Also listen for pushstate/replacestate (programmatic navigation)
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function (...args) {
			originalPushState.apply(window.history, args);
			handleLocationChange();
		};

		window.history.replaceState = function (...args) {
			originalReplaceState.apply(window.history, args);
			handleLocationChange();
		};

		// Fallback: check periodically in case events are missed
		const intervalId = setInterval(handleLocationChange, 1000);

		return () => {
			window.removeEventListener('popstate', handleLocationChange);
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
			clearInterval(intervalId);
		};
	}, []);

	return useMemo(() => {
		// Computed values for badge counts
		const pendingApprovalsCount = user.pending_awards_count || 0;
		const reportedPostsCount = user.unresolved_bonus_reports_count || 0;
		const recognitionBadgeCount = pendingApprovalsCount + reportedPostsCount;
		const hasHrisSyncError = !!(company as unknown as Record<string, unknown>).has_hris_sync_error && canAccessUserManagement;

		// One-on-Ones access - gated purely by product tier via Permission.CanAccessOneOnOnes

		// ===== COMPANY SECTION =====
		const companyLinks: NavLinkConfig[] = [
			{
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.company.account_settings',
				url: '/admin/company/settings',
			},
			{
				requirements: { roles: ['global_admin', 'tech_admin'] },
				label: 'admin_menu.company.integrations',
				matchActive: /\/company\/settings\/integrations/,
				url: adminIframePath(Route.Integrations),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.company.billing_settings',
				url: adminIframePath(Route.AdminBilling),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin'] },
				label: 'admin_menu.company.billing_history',
				url: adminIframePath(Route.AdminBillingActivity),
			},
			{
				requirements: { roles: ['global_admin', 'rewards_admin'] },
				matchActive: /\/admin\/rewards/,
				label: 'admin_menu.company.rewards_catalog',
				url: adminRewardsPath(AppPath.Catalog),
			},
			{
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.company.programs_management',
				url: '/admin/programs/settings',
			},
			{
				requirements: {
					roles: ['global_admin', 'announcements_admin'],
					customCheck: canAccessAnnouncements,
				},
				label: 'admin_menu.company.announcements',
				url: '/admin/company/announcements',
			},
		];

		// ===== USERS SECTION =====
		const userLinks: NavLinkConfig[] = [
			{
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.users.user_accounts',
				url: adminIframePath(Route.AdminUsers),
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canAccessUserManagement,
				},
				label: 'admin_menu.users.user_managment',
				showAlertDot: hasHrisSyncError,
				url: adminIframePath(company.user_management_integration_url || Route.AdminEmptyIntegration),
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canManageUserImport,
				},
				label: 'admin_menu.users.manage_user_import',
				url: adminIframePath(Route.AdminImportedUsers),
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canManageTeamRequests,
				},
				label: 'admin_menu.users.team_requests',
				matchActive: /\/admin\/users\/manage-team-requests/,
				url: '/admin/users/manage-team-requests',
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: hasManagerAssignmentTool,
				},
				label: 'admin_menu.users.team_overview',
				matchActive: /\/admin\/users\/team-overview/,
				url: '/admin/users/team-overview',
			},
		];

		// ===== RECOGNITION SECTION =====
		const recognitionLinks: NavLinkConfig[] = [
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canManagePeerToPeer,
				},
				label: 'admin_menu.recognition.peer_to_peer',
				url: '/admin/programs/peer-to-peer',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessAwards,
				},
				matchActive: /\/admin\/programs\/awards/,
				label: 'admin_menu.recognition.awards_management',
				url: '/admin/programs/awards',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessNominations,
				},
				label: 'admin_menu.recognition.nominations',
				matchActive: /\/admin\/programs\/nominations/,
				url: '/admin/programs/nominations',
			},
			{
				requirements: { roles: ['global_admin', 'awards_admin'] },
				label: 'admin_menu.recognition.celebrations',
				matchActive: /\/admin\/programs\/celebrations/,
				url: '/admin/programs/celebrations',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: canAccessIncentives,
				},
				label: 'admin_menu.recognition.incentives',
				matchActive: /\/admin\/programs\/incentives/,
				url: '/admin/programs/incentives',
			},
			{
				badgeCount: pendingApprovalsCount,
				requirements: { roles: ['global_admin', 'awards_admin', 'award_approver'] },
				label: 'admin_menu.recognition.pending',
				url: adminIframePath(Route.AdminAwardsPending),
			},
			{
				badgeCount: reportedPostsCount,
				requirements: { roles: ['global_admin'] },
				label: 'admin_menu.recognition.reported_posts',
				url: adminIframePath(Route.AdminBonusReports),
			},
		];

		// ===== GROWTH (ONE-ON-ONES) SECTION =====
		const oneOnOnesLinks: NavLinkConfig[] = [
			{
				requirements: {
					roles: ['global_admin', 'awards_admin', 'reports_admin'],
					customCheck: isOneOnOnesEnabled,
				},
				label: 'admin_menu.oneonones.overview',
				matchActive: /one-on-ones\/(?!logs)/,
				url: '/admin/growth/one-on-ones',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: isRecapsEnabled,
				},
				label: 'admin_menu.oneonones.recaps',
				matchActive: /performance-recap/,
				url: '/admin/growth/performance-recap',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin', 'reports_admin'],
					customCheck: canAccessGoals && isOneOnOnesEnabled,
				},
				label: 'admin_menu.oneonones.company_initiatives',
				matchActive: /company-initiatives/,
				url: '/admin/growth/company-initiatives',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin'],
					customCheck: isOneOnOnesEnabled && user.people_data_admin,
				},
				label: 'admin_menu.oneonones.logs',
				matchActive: /one-on-ones\/logs/,
				url: '/admin/growth/one-on-ones/logs',
			},
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canAccessAdminCheckInControls,
				},
				label: 'admin_menu.checkin_settings.title',
				matchActive: /pulse-check-settings/,
				url: '/admin/growth/pulse-check-settings',
			},
		];

		// ===== REPORTS SECTION =====
		const reportLinks: NavLinkConfig[] = [
			{
				requirements: { roles: ['global_admin', 'awards_admin', 'finance_admin', 'rewards_admin', 'reports_admin'] },
				label: 'admin_menu.reports.subscriptions',
				url: '/admin/subscriptions',
			},
			{
				requirements: {
					roles: ['global_admin', 'reports_admin'],
					customCheck: canViewExpandedReporting,
				},
				label: 'admin_menu.reports.participationByGroup',
				matchActive: /\/admin\/reports\/participation/,
				url: '/admin/reports/participation',
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin', 'reports_admin'],
					customCheck: canViewExpandedReporting,
				},
				label: 'admin_menu.reports.pointsActivity',
				matchActive: /\/admin\/reports\/points-activity/,
				url: '/admin/reports/points-activity',
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin', 'reports_admin'],
					customCheck: hasAnalyticsTier,
				},
				label: 'admin_menu.reports.collaborationMap',
				matchActive: /\/admin\/reports\/collaboration/,
				url: '/admin/reports/collaboration',
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin', 'reports_admin'],
					customCheck: canViewContinuousCheckIns,
				},
				label: 'admin_menu.reports.continuousCheckIns',
				url: '/admin/reports/pulse-results',
			},
			{
				requirements: { roles: ['global_admin', 'awards_admin', 'reports_admin'] },
				label: 'admin_menu.reports.recognition_activity',
				url: recognitionActivityRedesign ? Route.AdminRecognitionActivity : adminIframePath(Route.AdminLegacyRecognition),
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin', 'reports_admin'] },
				label: 'admin_menu.reports.recognitionScores',
				url: '/admin/reports/recognition_scores',
			},
			{
				requirements: {
					roles: ['global_admin', 'awards_admin', 'reports_admin'],
					customCheck: canAccessIncentives && canViewExpandedReporting,
				},
				label: 'admin_menu.reports.incentives_summary',
				url: '/admin/incentives/summary',
			},
			{
				requirements: { roles: ['global_admin', 'rewards_admin', 'reports_admin'] },
				label: 'admin_menu.reports.redemptions_activity',
				url: rewardsReportFrontend ? Route.AdminRedemptions : adminIframePath(Route.AdminRewards),
				matchActive: rewardsReportFrontend ? /\/company\/rewards\/redemptions/ : undefined,
			},
			{
				requirements: { roles: ['global_admin', 'finance_admin', 'reports_admin'] },
				label: 'admin_menu.reports.rewards_cost',
				url: rewardsReportFrontend ? Route.AdminTotals : adminIframePath(Route.AdminTotals),
				matchActive: rewardsReportFrontend ? /\/company\/rewards\/totals_report/ : undefined,
			},
		];

		// ===== LEGACY SECTION =====
		const legacyReportLinks: NavLinkConfig[] = [
			{
				requirements: {
					roles: ['global_admin'],
					customCheck: canViewExpandedReporting,
				},
				label: 'admin_menu.reports.participation_summary',
				url: recognizeURL(Route.AdminParticipation),
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: canViewExpandedReporting,
				},
				label: 'admin_menu.reports.points_given',
				url: recognizeURL(Route.AdminGiving),
			},
			{
				requirements: {
					roles: ['global_admin', 'finance_admin'],
					customCheck: canViewExpandedReporting,
				},
				label: 'admin_menu.reports.points_earned',
				url: recognizeURL(Route.AdminEarnings),
			},
		];

		// ===== ADMIN NAV CONFIG =====
		const adminSectionsUnfiltered: NavSectionConfig[] = [
			{
				icon: OfficeBuildingOutline,
				id: 'company-nav',
				title: 'admin_menu.company.title',
				links: companyLinks,
			},
			{
				icon: UserGroupOutline,
				id: 'users-nav',
				showAlertDot: hasHrisSyncError,
				title: 'admin_menu.users.title',
				links: userLinks,
			},
			{
				badgeCount: recognitionBadgeCount,
				icon: ChatAltOutline,
				id: 'recognition-nav',
				title: 'admin_menu.recognition.title',
				links: recognitionLinks,
			},
			{
				icon: PlantOutline,
				id: 'one-on-ones-nav',
				title: 'admin_menu.oneonones.title',
				links: oneOnOnesLinks,
			},
			{
				icon: DocumentReportOutline,
				id: 'reports-nav',
				title: 'admin_menu.reports.title',
				links: reportLinks,
			},
			{
				icon: HistoryOutline,
				id: 'legacy-nav',
				title: 'admin_menu.legacy.title',
				subsections: [
					{
						label: 'admin_menu.legacy.reports',
						links: legacyReportLinks,
						requirements: { customCheck: showLegacyReports },
					},
				],
			},
		];

		const adminLinksUnfiltered: NavLinkConfig[] = [
			{
				icon: ViewGridOutline,
				requirements: { roles: ['global_admin', 'finance_admin', 'reports_admin'] },
				label: 'admin_menu.dashboard',
				url: '/admin/dashboard',
			},
		];

		const admin: NavConfig = {
			id: 'admin-nav',
			label: 'admin_menu.menu_label',
			title: 'admin_menu.title',
			links: filterNavLinks(adminLinksUnfiltered, user),
			sections: filterNavSections(adminSectionsUnfiltered, user),
		};

		// Determine which section should be expanded based on the current URL
		const determineActiveSectionIndex = (nav: NavConfig): number | undefined => {
			const pathname = currentPathname;

			for (let i = 0; i < nav.sections.length; i++) {
				const section = nav.sections[i];

				// Helper function to check if a link matches the current pathname
				const linkMatchesPathname = (link: NavLinkConfig): boolean => {
					// For recognize routes, we need to extract the path from the full URL
					let linkPath = link.url;

					// If the link.url contains a full URL (from useBuildRecognizeURL), extract just the path
					if (link.url.includes('http')) {
						try {
							const url = new URL(link.url);
							linkPath = url.pathname;
						} catch (e) {
							// If it's not a valid URL, use the original value
							linkPath = link.url;
						}
					}

					// Check if the current pathname matches the link URL exactly
					if (linkPath === pathname) {
						return true;
					}

					// Check matchActive regex pattern
					if (link.matchActive && link.matchActive.test(pathname)) {
						return true;
					}

					return false;
				};

				// Check direct links
				if (section.links) {
					for (const link of section.links) {
						if (linkMatchesPathname(link)) {
							return i;
						}
					}
				}

				// Check subsection links
				if (section.subsections) {
					for (const subsection of section.subsections) {
						for (const link of subsection.links) {
							if (linkMatchesPathname(link)) {
								return i;
							}
						}
					}
				}
			}

			return undefined; // No active section found
		};

		admin.activeSectionIndex = determineActiveSectionIndex(admin);

		// ===== SUPER ADMIN NAV CONFIG =====
		const superAdminSectionsUnfiltered: NavSectionConfig[] = [
			{
				icon: OfficeBuildingOutline,
				id: 'company-nav',
				title: 'super_admin_menu.companies.title',
				links: [
					{
						label: 'super_admin_menu.companies.manage',
						url: Route.SuperAdminManageCompanies,
					},
					{
						label: 'super_admin_menu.companies.kyb',
						url: Route.SuperAdminKyb,
					},
					{
						label: 'super_admin_menu.companies.sign_ups',
						url: Route.SuperAdminCompanySignUps,
					},
					{
						label: 'super_admin_menu.companies.integrations',
						url: Route.SuperAdminCompanyIntegrations,
					},
					{
						label: 'External Integrations',
						url: Route.SuperAdminExternalIntegrations,
					},
					{
						label: 'super_admin_menu.companies.integrations_health',
						url: Route.SuperAdminIntegrationsHealth,
					},
				],
			},
			{
				icon: UserGroupOutline,
				id: 'users-nav',
				title: 'super_admin_menu.users.title',
				links: [
					{
						label: 'super_admin_menu.users.manage',
						url: Route.SuperAdminManageUsers,
					},
					{
						label: 'super_admin_menu.users.alias',
						url: Route.SuperAdminAliasUsers,
					},
				],
			},
			{
				icon: GiftOutline,
				id: 'rewards-nav',
				title: 'super_admin_menu.rewards.title',
				links: [
					{
						label: 'super_admin_menu.rewards.manage',
						url: Route.SuperAdminManageRewards,
					},
					{
						label: 'super_admin_menu.rewards.health',
						url: Route.SuperAdminRewardsHealth,
					},
					{
						label: 'super_admin_menu.rewards.tango_orders',
						url: Route.SuperAdminTangoOrders,
					},
					{
						label: 'super_admin_menu.rewards.manually_fulfilled',
						url: Route.SuperAdminManuallyFulfilled,
					},
				],
			},
			{
				icon: BookAltOutline,
				id: 'catalog-nav',
				title: 'super_admin_menu.catalog.title',
				links: [
					{
						label: 'super_admin_menu.catalog.manage',
						url: Route.SuperAdminManageCatalog,
					},
					{
						label: 'super_admin_menu.catalog.providers',
						url: Route.SuperAdminProviders,
					},
					{
						label: 'super_admin_menu.catalog.lint',
						url: Route.SuperAdminLint,
					},
				],
			},
			{
				icon: PuzzleOutline,
				id: 'developer-nav',
				title: 'super_admin_menu.developer.title',
				links: [
					{
						label: 'super_admin_menu.developer.marmon_sftp',
						url: Route.SuperAdminMarmonSFTP,
					},
					{
						label: 'super_admin_menu.developer.videos',
						url: Route.SuperAdminVideos,
					},
					{
						label: 'super_admin_menu.developer.internals',
						url: Route.SuperAdminInternals,
					},
					{
						label: 'super_admin_menu.developer.email',
						url: Route.SuperAdminEmail,
					},
					{
						label: 'Testing',
						url: '/super_admin/testing/spotlights',
					},
					{
						label: 'Feature Requests',
						url: Route.SuperAdminBizyFeatureRequests,
						matchActive: /\/super_admin\/bizy\/feature-requests/,
					},
				],
			},
			{
				icon: LifeRaftOutline,
				id: 'quality-ops-nav',
				title: 'super_admin_menu.quality_ops.title',
				links: [
					{
						requirements: { roles: ['tech_ops_admin'] },
						label: 'super_admin_menu.quality_ops.everything',
						url: adminIframePath(Route.TechOps, '/super_admin'),
					},
					{
						requirements: { roles: ['tech_ops_admin'] },
						label: 'super_admin_menu.quality_ops.company_specific',
						url: adminIframePath(Route.TechOpsCompany, '/super_admin'),
					},
					{
						requirements: { roles: ['tech_ops_admin'] },
						label: 'super_admin_menu.quality_ops.user_specific',
						url: adminIframePath(Route.TechOpsUser, '/super_admin'),
					},
				],
			},
			{
				icon: ChartBarOutline,
				id: 'kvms-nav',
				title: 'super_admin_menu.kvms.title',
				links: [
					{
						label: 'super_admin_menu.kvms.giving_rate',
						url: Route.SuperAdminRecognitionKVM,
					},
					{
						label: 'super_admin_menu.kvms.redemptions_per_user',
						url: Route.SuperAdminRedemptionsPerUserKVM,
					},
					{
						label: 'super_admin_menu.kvms.one_on_one',
						url: Route.SuperAdminOneOnOneKVM,
					},
					{
						label: 'super_admin_menu.kvms.pulse_checks',
						url: Route.SuperAdminPulseChecksKVM,
					},
					{
						label: 'super_admin_menu.kvms.insights',
						url: Route.SuperAdminInsightsKVM,
					},
					{
						label: 'super_admin_menu.kvms.app_performance',
						url: Route.SuperAdminAppPerformance,
					},
				],
			},
			{
				icon: ChatAltOutline,
				id: 'bizy-nav',
				title: 'super_admin_menu.bizy.title',
				links: [
					{
						label: 'super_admin_menu.bizy.trends',
						url: Route.SuperAdminBizyTrends,
						matchActive: /\/super_admin\/bizy\/trends/,
					},
					{
						label: 'super_admin_menu.bizy.topics',
						url: Route.SuperAdminBizyTopics,
						matchActive: /\/super_admin\/bizy\/topics/,
					},
					{
						label: 'super_admin_menu.bizy.conversations',
						url: Route.SuperAdminBizyConversations,
						matchActive: /\/super_admin\/bizy\/conversations(?!.*training)/,
					},
					{
						label: 'super_admin_menu.bizy.training',
						url: Route.SuperAdminBizyTrainingConversations,
						matchActive: /\/super_admin\/bizy\/training_conversations/,
					},
					{
						label: 'super_admin_menu.bizy.draft_assist_outcomes',
						url: Route.SuperAdminDraftAssistOutcomes,
						matchActive: /\/super_admin\/kvms\/bizy\/draft-assist-outcomes/,
					},
					{
						label: 'super_admin_menu.bizy.feedback',
						url: Route.SuperAdminBizyFeedback,
						matchActive: /\/super_admin\/bizy\/feedback/,
					},
					{
						label: 'super_admin_menu.bizy.triggers',
						url: Route.SuperAdminBizyTriggers,
						matchActive: /\/super_admin\/bizy\/triggers/,
					},
				],
			},
			{
				icon: PlantOutline,
				id: 'habits-bu-nav',
				title: 'super_admin_menu.habits_bu.title',
				links: [
					{
						label: 'super_admin_menu.habits_bu.trial_conversion',
						url: Route.SuperAdminTrialConversion,
						matchActive: /\/super_admin\/kvms\/trial-conversion/,
					},
					{
						label: 'super_admin_menu.habits_bu.one_on_one_activation',
						url: Route.SuperAdminOneOnOneActivation,
						matchActive: /\/super_admin\/kvms\/one-on-one-activation/,
					},
					{
						label: 'super_admin_menu.habits_bu.activation_dashboard',
						url: Route.SuperAdminActivationDashboard,
						matchActive: /\/super_admin\/kvms\/activation/,
					},
					{
						label: 'super_admin_menu.habits_bu.transcript_metrics',
						url: Route.SuperAdminTranscriptMetrics,
						matchActive: /\/super_admin\/kvms\/transcript-metrics/,
					},
					{
						label: 'super_admin_menu.habits_bu.notification_metrics',
						url: Route.SuperAdminNotificationMetrics,
						matchActive: /\/super_admin\/kvms\/notification-metrics/,
					},
					{
						label: 'super_admin_menu.habits_bu.goals_adoption',
						url: Route.SuperAdminGoalsAdoption,
						matchActive: /\/super_admin\/kvms\/goals-adoption/,
					},
					{
						label: 'super_admin_menu.habits_bu.advice_adoption',
						url: Route.SuperAdminAdviceAdoption,
						matchActive: /\/super_admin\/kvms\/advice-adoption/,
					},
					{
						label: 'super_admin_menu.habits_bu.one_on_one_adoption',
						url: Route.SuperAdminOneOnOneAdoption,
						matchActive: /\/super_admin\/kvms\/one-on-one-adoption/,
					},
					{
						label: '1:1 Suggestions',
						url: Route.SuperAdminConversationSuggestions,
						matchActive: /\/super_admin\/one_on_ones\/conversation_suggestions/,
					},
				],
			},
			{
				icon: ShieldCheckOutline,
				id: 'security-nav',
				title: 'Security',
				links: [
					{
						label: 'Blocklisted Companies',
						url: Route.SuperAdminBlocklistedCompanies,
					},
					{
						label: 'Blocklisted IP Addresses',
						url: Route.SuperAdminBlocklistedIpAddresses,
					},
					{
						label: 'Blocklisted Email Domains',
						url: Route.SuperAdminBlocklistedEmailDomains,
					},
					{
						label: 'Passlisted IP Addresses',
						url: Route.SuperAdminPasslistedIpAddresses,
					},
					{
						label: 'Scheduled Point Events',
						url: Route.SuperAdminScheduledPointEvents,
					},
				],
			},
		];

		// Filter super admin sections based on user role
		const hasSuperAdminRole = user.show_superadmin_feature;
		const superAdmin: NavConfig = {
			id: 'super-admin-nav',
			label: 'super_admin_menu.menu_label',
			title: 'super_admin_menu.title',
			roles: ['show_superadmin_feature'],
			sections: hasSuperAdminRole ? filterNavSections(superAdminSectionsUnfiltered, user) : [],
		};

		superAdmin.activeSectionIndex = determineActiveSectionIndex(superAdmin);

		return { admin, superAdmin };
	}, [
		company,
		user,
		recognizeURL,
		canAccessAdminCheckInControls,
		canAccessAnnouncements,
		canAccessAwards,
		canAccessGoals,
		canAccessIncentives,
		canAccessNominations,
		canAccessUserManagement,
		canManagePeerToPeer,
		canManageUserImport,
		canViewContinuousCheckIns,
		canViewExpandedReporting,
		currentPathname,
		hasAnalyticsTier,
		isOneOnOnesEnabled,
		recognitionActivityRedesign,
		rewardsReportFrontend,
		isRecapsEnabled,
		showLegacyReports,
		canManageTeamRequests,
		hasManagerAssignmentTool,
	]);
};
